import { useState, useEffect } from "react";
import { X, UserPlus, Save, Loader2 } from "lucide-react";
import { CustomSelect } from "./common/CustomSelect";

export default function NewPatronModal({ onClose, onSave, initialData = null }) {
  const [formData, setFormData] = useState({
    cardnumber: "",
    firstname: "",
    surname: "",
    title: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!initialData;

  // Populate data if opened in Edit Mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        cardnumber: initialData.cardnumber || "",
        firstname: initialData.firstname || "",
        surname: initialData.surname || "",
        title: initialData.title || "",
      });
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cardnumber || !formData.firstname || !formData.surname) return;

    try {
      setIsSubmitting(true);
      await onSave(formData);

      if (!isEditMode) {
        setFormData({ cardnumber: "", firstname: "", surname: "", title: "" });
      }
      onClose();
    } catch (err) {
      console.error("Modal submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const TITLE_OPTIONS = [
    { label: "Select...", value: "" },
    { label: "Mr.", value: "Mr" },
    { label: "Ms.", value: "Ms" },
    { label: "Mrs.", value: "Mrs" },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-dynamic-title animate-fadeIn">
      <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all">
        
        {/* MODAL HEADER */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-stone-100">
                {isEditMode ? "Modify Patron Profile" : "Register New Patron"}
              </h3>
              <p className="text-xs text-stone-400 mt-0.5">
                {isEditMode
                  ? "Update configuration for this account holder."
                  : "Add a new institutional borrower profile."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-100 hover:bg-[#2e2720] p-1.5 rounded-xl transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* FORM BODY */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs font-semibold">
          
          {/* Card Identification Number */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-stone-400 uppercase tracking-wider">
              Card Identification Number <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              className="w-full bg-[#131110] border border-[#2e2720] px-3.5 py-2.5 rounded-xl text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition font-mono text-xs tracking-wide"
              placeholder="e.g. CAIS-2026-X9"
              required
              value={formData.cardnumber}
              onChange={(e) => setFormData({ ...formData, cardnumber: e.target.value })}
            />
          </div>

          {/* Title & First Name */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-1">
              <label className="text-[11px] font-bold text-stone-400 uppercase tracking-wider">
                Honorific
              </label>

              <CustomSelect
                value={formData.title}
                onChange={(val) => setFormData({ ...formData, title: val })}
                options={TITLE_OPTIONS}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-[11px] font-bold text-stone-400 uppercase tracking-wider">
                First Given Name <span className="text-amber-500">*</span>
              </label>
              <input
                type="text"
                className="w-full bg-[#131110] border border-[#2e2720] px-3.5 py-2.5 rounded-xl text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition text-xs"
                placeholder="First name"
                required
                value={formData.firstname}
                onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
              />
            </div>
          </div>

          {/* Surname */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-stone-400 uppercase tracking-wider">
              Family Name / Surname <span className="text-amber-500">*</span>
            </label>
            <input
              type="text"
              className="w-full bg-[#131110] border border-[#2e2720] px-3.5 py-2.5 rounded-xl text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition text-xs"
              placeholder="Last name"
              required
              value={formData.surname}
              onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
            />
          </div>

          {/* ACTION BUTTONS */}
          <div className="pt-4 flex items-center justify-end gap-2.5 border-t border-[#2e2720] mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 h-9 bg-[#131110] font-mono border border-[#2e2720] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] rounded-xl disabled:opacity-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center font-mono gap-2 px-5 h-9 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-xl shadow disabled:opacity-50 transition"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>{isEditMode ? "Update Profile" : "Create Profile"}</span>
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}